import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Box,
  makeStyles,
} from '@material-ui/core';
import {
  TrendingUp,
  TrendingDown,
  People,
  Message,
  Timer,
  AttachMoney,
  CheckCircle,
} from '@material-ui/icons';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
  },
  metricChange: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
  positiveChange: {
    color: theme.palette.success.main,
  },
  negativeChange: {
    color: theme.palette.error.main,
  },
  neutralChange: {
    color: theme.palette.text.secondary,
  },
  progressContainer: {
    marginTop: theme.spacing(1),
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
  },
  icon: {
    fontSize: '2rem',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  realtimeIndicator: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
}));

const MetricCard = ({
  title,
  value,
  change,
  icon: Icon,
  goal,
  realtime = false,
}) => {
  const classes = useStyles();

  const formatValue = val => {
    // Se for string, tentar converter para número
    if (typeof val === 'string') {
      // Remover formatação incorreta e converter para número
      const cleanValue = val.replace(',', '.');
      const numValue = parseFloat(cleanValue);
      if (!isNaN(numValue)) {
        val = numValue;
      } else {
        return val; // Retornar string original se não conseguir converter
      }
    }

    if (typeof val === 'number') {
      if (val >= 1000000) {
        return `${(val / 1000000).toFixed(1)}M`;
      } else if (val >= 1000) {
        return `${(val / 1000).toFixed(1)}K`;
      }
      return val.toLocaleString('pt-BR');
    }
    return val;
  };

  const formatChange = changeValue => {
    if (!changeValue) return null;

    const isPositive = changeValue > 0;
    const isNegative = changeValue < 0;

    return (
      <div
        className={`${classes.metricChange} ${
          isPositive
            ? classes.positiveChange
            : isNegative
              ? classes.negativeChange
              : classes.neutralChange
        }`}
      >
        {isPositive ? (
          <TrendingUp fontSize='small' />
        ) : isNegative ? (
          <TrendingDown fontSize='small' />
        ) : null}
        <span style={{ marginLeft: 4 }}>
          {isPositive ? '+' : ''}
          {changeValue}%
        </span>
      </div>
    );
  };

  const calculateProgress = () => {
    if (!goal || !value) return 0;
    return Math.min((value / goal) * 100, 100);
  };

  return (
    <Card className={classes.card}>
      {/* {realtime && <div className={classes.realtimeIndicator} />} */}
      <CardContent className={classes.cardContent}>
        <Icon className={classes.icon} />
        <Typography variant='h4' className={classes.metricValue}>
          {value}
        </Typography>
        <Typography variant='body2' className={classes.metricLabel}>
          {title}
        </Typography>
        {formatChange(change)}
        {goal && (
          <div className={classes.progressContainer}>
            <LinearProgress
              variant='determinate'
              value={calculateProgress()}
              className={classes.progressBar}
              color={calculateProgress() >= 100 ? 'secondary' : 'primary'}
            />
            <Typography variant='caption' color='textSecondary'>
              {i18n.t('metrics.goalProgress')}: {formatValue(value)} /{' '}
              {formatValue(goal)}
            </Typography>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MetricsCards = ({ metrics, goals = null, realtimeMetrics }) => {
  const classes = useStyles();

  if (!metrics && !realtimeMetrics) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant='body1' color='textSecondary'>
            {i18n.t('metrics.loading')}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  const conversionMetrics = metrics?.conversionMetrics || {};
  const leadsMetrics = metrics?.leadsMetrics || {};
  const messageMetrics = metrics?.messageMetrics || {};
  const realtime = realtimeMetrics || {};

  // Encontrar metas ativas para cada métrica
  const getActiveGoal = metricType => {
    if (!goals || !Array.isArray(goals)) return null;
    const activeGoal = goals.find(
      goal => goal.metric === metricType && goal.active
    );
    return activeGoal?.target || null;
  };

  return (
    <Grid container spacing={3}>
      {/* Métricas de Conversão */}
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title={i18n.t('metrics.cards.conversions')}
          value={conversionMetrics.totalConversions || 0}
          change={conversionMetrics.conversionChange}
          icon={TrendingUp}
          goal={getActiveGoal('conversion')}
          realtime={true}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title={i18n.t('metrics.cards.totalValue')}
          value={new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(conversionMetrics.totalValue || 0)}
          change={conversionMetrics.valueChange}
          icon={AttachMoney}
          goal={getActiveGoal('financial')}
          realtime={true}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title={i18n.t('metrics.cards.conversionRate')}
          value={`${(conversionMetrics.conversionRate || 0).toFixed(1)} %`}
          change={conversionMetrics.rateChange}
          icon={TrendingUp}
          realtime={true}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title={i18n.t('metrics.cards.avgConversionTime')}
          value={`${Math.round(realtimeMetrics?.averageConversionTime || 0)} min`}
          change={conversionMetrics.timeChange}
          icon={Timer}
          realtime={true}
        />
      </Grid>

      {/* Métricas de Leads */}
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title={i18n.t('metrics.cards.totalLeads')}
          value={leadsMetrics.totalLeads || 0}
          change={leadsMetrics.leadsChange}
          icon={People}
          goal={getActiveGoal('leads')}
          realtime={true}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title={i18n.t('metrics.cards.newLeads')}
          value={leadsMetrics.newLeads || 0}
          change={leadsMetrics.newLeadsChange}
          icon={People}
          realtime={true}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title={i18n.t('metrics.cards.qualifiedLeads')}
          value={leadsMetrics.qualifiedLeads || 0}
          change={leadsMetrics.qualifiedLeadsChange}
          icon={People}
          realtime={true}
        />
      </Grid>

      {/* Métricas de Mensagens */}
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          title={i18n.t('metrics.cards.totalMessages')}
          value={messageMetrics.totalMessages || 0}
          change={messageMetrics.messagesChange}
          icon={Message}
          goal={getActiveGoal('messages')}
          realtime={true}
        />
      </Grid>

      {/* Métricas em Tempo Real */}
      {realtimeMetrics && (
        <>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title={i18n.t('metrics.cards.activeTickets')}
              value={metrics?.activeTickets || 0}
              icon={People}
              realtime={false}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title={i18n.t('metrics.cards.pendingTickets')}
              value={metrics?.pendingTickets || 0}
              icon={Timer}
              realtime={false}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title={i18n.t('dashboard.counters.completedTickets')}
              value={metrics?.completedTickets || 0}
              icon={CheckCircle}
              realtime={true}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title={i18n.t('metrics.cards.avgResponseTime')}
              value={`${Math.round(realtimeMetrics?.averageResponseTime || 0)} min`}
              icon={Timer}
              realtime={true}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default MetricsCards;
